import { Outlet, NavLink, useNavigate } from 'react-router-dom';

import TabBar from '~/atomic/molecules/TabBar/view';
import Avatar from '~/atomic/atoms/Avatar/view';
import { Icon } from '~/atomic/atoms/Icon/view';
import { Text } from '~/atomic/atoms/Text/view';
import type { propsIconsNames } from '~/atomic/atoms/Icon/interfaces';
import { useAppDispatch, useAppSelector } from '~/store';
import { selectOverdueInstallments } from '~/store/contracts/selectors';
import { selectUserData } from '~/store/user/selectors';
import { logoutUser } from '~/store/auth/login/thunks';
import { cn } from '~/utils/cn';

interface NavItem {
  to: string;
  label: string;
  icon: propsIconsNames;
}

const PRIMARY_NAV: NavItem[] = [
  { to: '/app/home', label: 'Início', icon: 'icon-home' },
  { to: '/app/contracts', label: 'Contratos', icon: 'icon-document' },
  { to: '/app/alerts', label: 'Alertas', icon: 'icon-notification' },
  { to: '/app/market', label: 'Mercado', icon: 'icon-graphic-up' },
  { to: '/app/reports', label: 'Relatórios', icon: 'icon-extract' },
  { to: '/app/ferramentas/calculadora', label: 'Ferramentas', icon: 'icon-graphic-up' },
];

const ACCOUNT_NAV: NavItem[] = [
  { to: '/app/profile', label: 'Perfil', icon: 'icon-user' },
];

function SidebarItem({ item, badge }: { item: NavItem; badge?: number }) {
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center gap-smaller pl-smaller pr-smaller py-[10px] rounded-small transition-all',
          isActive ? 'bg-support-01' : 'hover:bg-intense-02',
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive ? (
            <span className="absolute left-[-12px] top-1/2 -translate-y-1/2 w-[3px] h-[22px] rounded-r-full bg-primary-01" />
          ) : null}
          <span
            className={cn(
              'h-[32px] w-[32px] rounded-nano flex items-center justify-center transition-colors shrink-0',
              isActive
                ? 'bg-primary-01'
                : 'bg-intense-02 group-hover:bg-intense-03',
            )}
          >
            <Icon
              name={item.icon}
              size={16}
              color={isActive ? '#FFFFFF' : '#1D4733'}
              rounded={false}
            />
          </span>
          <Text
            className={cn(
              'flex-1 text-bodyLG min-w-0',
              isActive ? 'color-primary-01' : 'color-intense-07',
            )}
            numberOfLines={1}
          >
            {item.label}
          </Text>
          {badge && badge > 0 ? (
            <span className="min-w-[20px] h-[20px] px-[6px] rounded-full bg-warning-03 flex items-center justify-center shrink-0">
              <Text inline className="text-captionLG color-intense-01">
                {badge > 99 ? '99+' : String(badge)}
              </Text>
            </span>
          ) : null}
        </>
      )}
    </NavLink>
  );
}

export default function AppShell() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(selectUserData);
  const overdues = useAppSelector(selectOverdueInstallments);
  const overdueCount = overdues.length;

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate('/auth/login', { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-intense-02">
      <aside className="hidden xl:flex w-[272px] flex-col bg-intense-01 border-r border-intense-03 fixed left-0 top-0 bottom-0 z-20">
        <div className="px-medium pt-medium pb-medium border-b border-intense-03">
          <div className="flex items-center gap-smaller">
            <div className="relative h-[44px] w-[44px] rounded-medium bg-intense-01 border border-intense-03 flex items-center justify-center overflow-hidden shrink-0">
              <img
                src="/logo.png"
                alt="Paga Agro"
                className="h-full w-full object-contain"
              />
              <span className="absolute -top-[2px] -right-[2px] w-[10px] h-[10px] rounded-full bg-success-03 border-2 border-intense-01" />
            </div>
            <div className="flex-1 min-w-0">
              <Text
                className="text-paragraphLG color-complement-01"
                numberOfLines={1}
              >
                Paga Agro
              </Text>
              <Text
                className="text-captionMD color-intense-06"
                numberOfLines={1}
              >
                {user.farm?.farmName ?? 'Minha fazenda'}
              </Text>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-smaller py-medium flex flex-col gap-medium">
          <div className="flex flex-col gap-quark">
            <Text className="px-smaller pb-quark text-captionMD color-intense-05 uppercase tracking-wider">
              Principal
            </Text>
            {PRIMARY_NAV.map((item) => (
              <SidebarItem
                key={item.to}
                item={item}
                badge={item.to === '/app/alerts' ? overdueCount : undefined}
              />
            ))}
          </div>

          <div className="flex flex-col gap-quark">
            <Text className="px-smaller pb-quark text-captionMD color-intense-05 uppercase tracking-wider">
              Conta
            </Text>
            {ACCOUNT_NAV.map((item) => (
              <SidebarItem key={item.to} item={item} />
            ))}
          </div>
        </nav>

        <div className="border-t border-intense-03 p-smaller">
          <div className="flex items-center gap-smaller p-smaller rounded-small bg-intense-02">
            <Avatar
              name={user.fullName}
              seed={user.fullName}
              size="sm"
            />
            <div className="flex-1 min-w-0">
              <Text
                className="text-bodyMD color-complement-01"
                numberOfLines={1}
              >
                {user.fullName || 'Produtor'}
              </Text>
              <Text
                className="text-captionMD color-intense-06"
                numberOfLines={1}
              >
                {user.contact?.email || '—'}
              </Text>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              title="Sair"
              aria-label="Sair"
              className="h-[36px] w-[36px] rounded-nano flex items-center justify-center bg-intense-01 border border-intense-03 hover:bg-warning-01 hover:border-warning-02 transition-colors shrink-0"
            >
              <Icon
                name="icon-logout"
                size={16}
                color="#992929"
                rounded={false}
              />
            </button>
          </div>
        </div>
      </aside>
      <div className="flex-1 min-w-0 xl:ml-[272px] min-h-screen">
        <Outlet />
      </div>
      <TabBar />
    </div>
  );
}
